<template>
  <div class="absolute bottom-2 z-20" :style="{ left: axisIndicatorWidth }">
    <canvas ref="canvasRef" :width="AXIS_INDICATOR_SIZE" :height="AXIS_INDICATOR_SIZE" class="pointer-events-none" />
  </div>
</template>

<script setup>
import {
  AXIS_CENTER_RADIUS,
  AXIS_COLOR_CENTER,
  AXIS_COLOR_X,
  AXIS_COLOR_Y,
  AXIS_COLOR_Z,
  AXIS_HEAD_LENGTH,
  AXIS_HEAD_WIDTH,
  AXIS_INDICATOR_CAMERA_BOUNDS,
  AXIS_INDICATOR_CAMERA_DISTANCE,
  AXIS_INDICATOR_CAMERA_FAR,
  AXIS_INDICATOR_CAMERA_NEAR,
  AXIS_INDICATOR_SIZE,
  AXIS_LABEL_CANVAS_SIZE,
  AXIS_LABEL_FONT,
  AXIS_LABEL_OFFSET,
  AXIS_LABEL_SCALE,
  AXIS_LENGTH,
  AXIS_SHAFT_RADIUS,
} from '@/constants/axisIndicator';
import { useAppStore } from '@/stores/appStore';
import { camera } from '@/threejs/core';
import * as THREE from 'three';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const canvasRef = ref(null);
const appStore = useAppStore();
let indicatorRenderer = null;
let indicatorScene = null;
let indicatorCamera = null;
let animationFrameId = null;

function initIndicator() {
  indicatorScene = new THREE.Scene();
  indicatorCamera = new THREE.OrthographicCamera(
    -AXIS_INDICATOR_CAMERA_BOUNDS,
    AXIS_INDICATOR_CAMERA_BOUNDS,
    AXIS_INDICATOR_CAMERA_BOUNDS,
    -AXIS_INDICATOR_CAMERA_BOUNDS,
    AXIS_INDICATOR_CAMERA_NEAR,
    AXIS_INDICATOR_CAMERA_FAR
  );

  const createAxis = (dir, color) => {
    const group = new THREE.Group();

    const shaftLen = AXIS_LENGTH - AXIS_HEAD_LENGTH;
    const shaftGeo = new THREE.CylinderGeometry(AXIS_SHAFT_RADIUS, AXIS_SHAFT_RADIUS, shaftLen, 8);
    const shaftMat = new THREE.MeshBasicMaterial({ color });
    const shaft = new THREE.Mesh(shaftGeo, shaftMat);
    shaft.position.copy(dir.clone().multiplyScalar(shaftLen / 2));
    shaft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    group.add(shaft);

    const coneGeo = new THREE.ConeGeometry(AXIS_HEAD_WIDTH, AXIS_HEAD_LENGTH, 12);
    const coneMat = new THREE.MeshBasicMaterial({ color });
    const cone = new THREE.Mesh(coneGeo, coneMat);
    cone.position.copy(dir.clone().multiplyScalar(AXIS_LENGTH - AXIS_HEAD_LENGTH / 2));
    cone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    group.add(cone);

    const canvas = document.createElement('canvas');
    canvas.width = AXIS_LABEL_CANVAS_SIZE;
    canvas.height = AXIS_LABEL_CANVAS_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.font = AXIS_LABEL_FONT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#' + new THREE.Color(color).getHexString();
    const label = dir.x ? 'X' : dir.y ? 'Y' : 'Z';
    ctx.fillText(label, AXIS_LABEL_CANVAS_SIZE / 2, AXIS_LABEL_CANVAS_SIZE / 2);
    const texture = new THREE.CanvasTexture(canvas);
    const spriteMat = new THREE.SpriteMaterial({ map: texture, depthTest: false });
    const sprite = new THREE.Sprite(spriteMat);
    sprite.position.copy(dir.clone().multiplyScalar(AXIS_LENGTH + AXIS_LABEL_OFFSET));
    sprite.scale.set(AXIS_LABEL_SCALE, AXIS_LABEL_SCALE, AXIS_LABEL_SCALE);
    sprite.renderOrder = 1;
    group.add(sprite);

    return group;
  };

  const sphereGeo = new THREE.SphereGeometry(AXIS_CENTER_RADIUS, 16, 16);
  const sphereMat = new THREE.MeshBasicMaterial({ color: AXIS_COLOR_CENTER });
  indicatorScene.add(new THREE.Mesh(sphereGeo, sphereMat));

  indicatorScene.add(createAxis(new THREE.Vector3(1, 0, 0), AXIS_COLOR_X));
  indicatorScene.add(createAxis(new THREE.Vector3(0, 1, 0), AXIS_COLOR_Y));
  indicatorScene.add(createAxis(new THREE.Vector3(0, 0, 1), AXIS_COLOR_Z));
}

function animate() {
  indicatorCamera.position.set(0, 0, AXIS_INDICATOR_CAMERA_DISTANCE);
  indicatorCamera.position.applyQuaternion(camera.quaternion);
  indicatorCamera.quaternion.copy(camera.quaternion);
  indicatorRenderer.render(indicatorScene, indicatorCamera);
  animationFrameId = requestAnimationFrame(animate);
}

onMounted(() => {
  indicatorRenderer = new THREE.WebGLRenderer({
    canvas: canvasRef.value,
    alpha: true,
    antialias: true,
  });
  indicatorRenderer.setSize(AXIS_INDICATOR_SIZE, AXIS_INDICATOR_SIZE);
  indicatorRenderer.setPixelRatio(window.devicePixelRatio);
  indicatorRenderer.setClearColor(0x000000, 0);

  initIndicator();
  animate();
});

const axisIndicatorWidth = computed(() => {
  return `${appStore.leftSettingPanelWidth + 2}px`;
});

onBeforeUnmount(() => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
  if (indicatorScene) {
    indicatorScene.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (obj.material.map) obj.material.map.dispose();
        obj.material.dispose();
      }
    });
  }
  if (indicatorRenderer) indicatorRenderer.dispose();
});
</script>
